import React, {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
} from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import log from 'loglevel';

import { useServices } from '@/components/ServiceContext';
import { UserContext } from '@/context/userContext';
import { isUserInGroup } from '@/auth/EricksonAuthenticator';
import { CognitoGroup, CognitoUser } from '@/repository/CognitoUserRepository';
import { ServiceDataGrid } from '@/components/ServiceDataGrid';

interface CognitoUserRow extends CognitoUser {
  Username: string;
  Attributes?: { Name: string; Value: string }[];
  /** For convenience, store Groups as an array of { GroupName: string }. */
  Groups?: CognitoGroup[];
}

const AdminPage: React.FC = () => {
  const { cognitoUserService } = useServices();
  const userContext = useContext(UserContext);

  // Load all available group names from Cognito
  const [groups, setGroups] = useState<string[]>([]);

  useEffect(() => {
    cognitoUserService.listGroups().then((cognitoGroups: CognitoGroup[]) => {
      setGroups(cognitoGroups.map(g => g.GroupName));
    });
  }, [cognitoUserService]);

  // Tracks which rows are in edit mode vs. view mode
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  /**
   * Prevent leaving edit mode on row blur
   */
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event
  ) => {
    if (params.reason === 'rowFocusOut') {
      event.defaultMuiPrevented = true;
    }
  };

  /**
   * Called when the user clicks the "Edit" button
   */
  const handleEditClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel(prev => ({
        ...prev,
        [id]: { mode: GridRowModes.Edit },
      }));
    },
    []
  );

  /**
   * Called when the user clicks the "Save" button
   */
  const handleSaveClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel(prev => ({
        ...prev,
        [id]: { mode: GridRowModes.View },
      }));
    },
    []
  );

  /**
   * Called when the user clicks the "Cancel" button
   */
  const handleCancelClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel(prev => ({
        ...prev,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      }));
    },
    []
  );

  /**
   * In row-editing mode, MUI calls processRowUpdate(newRow, oldRow)
   * when the user presses Save. This is where we:
   *  1. Diff the old vs. new groups
   *  2. For each group added => call addUserToGroup
   *  3. For each group removed => call removeUserFromGroup
   *  4. Optionally fetch updated user from Cognito
   * Return the "final" row object to MUI so it updates the DataGrid.
   */
  const processRowUpdate = useCallback(
    async (newRow: CognitoUserRow, oldRow: CognitoUserRow) => {
      try {
        // Arrays of strings for easier diff
        const oldGroups = oldRow.Groups?.map(g => g.GroupName) || [];
        const newGroups = newRow.Groups?.map(g => g.GroupName) || [];

        // Which groups were added or removed?
        const groupsToAdd = newGroups.filter(g => !oldGroups.includes(g));
        const groupsToRemove = oldGroups.filter(g => !newGroups.includes(g));

        // Username is our key
        const username = newRow.Username;

        // Make the calls to add/remove groups
        for (const groupName of groupsToAdd) {
          await cognitoUserService.addUserToGroup(username, groupName);
        }
        for (const groupName of groupsToRemove) {
          await cognitoUserService.removeUserFromGroup(username, groupName);
        }

        // Return the "updated" row so DataGrid can re-render with new data
        return newRow;
      } catch (error) {
        log.error('Error updating groups:', error);
        // Throw so MUI will revert to the old row
        throw error;
      }
    },
    [cognitoUserService]
  );

  /**
   * Rendering "Groups" as chips in read mode
   */
  const renderGroupsCell = useCallback(
    (
      params: GridRenderCellParams<CognitoUserRow, CognitoGroup[] | undefined>
    ) => {
      const groupsData = params.value || [];
      const groupNames = groupsData.map(g => g.GroupName);

      if (!groupNames.length) {
        return null;
      }

      return (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          sx={{ padding: 1.75 }}
        >
          {groupNames.map((groupName: string) => (
            <Chip key={groupName} label={groupName} size="small" />
          ))}
        </Stack>
      );
    },
    []
  );

  /**
   * Editing "Groups" via a multiple-select Autocomplete.
   * Only valid groups can be chosen.
   */
  const renderGroupsEditCell = useCallback(
    (
      params: GridRenderEditCellParams<
        CognitoUserRow,
        CognitoGroup[] | undefined
      >
    ) => {
      const { id, value, api, field } = params;
      const userEmail = userContext.user?.email;
      const rowEmail = params.row.Attributes?.find(
        a => a.Name === 'email'
      )?.Value;

      const currentSelections = (value || []).map(g => g.GroupName);

      const handleChange = (
        _event: SyntheticEvent<Element, Event>,
        newValues: string[]
      ) => {
        // Convert back to {GroupName: string}[]
        const newGroups = newValues.map(groupName => ({
          GroupName: groupName,
        }));
        api.setEditCellValue({ id, field, value: newGroups });
      };

      return (
        <Autocomplete
          multiple
          options={groups.filter(g => !currentSelections.includes(g))} // Exclude already selected groups
          value={currentSelections}
          onChange={handleChange}
          renderInput={params => (
            <TextField
              {...params}
              variant="standard"
              placeholder="Select groups"
            />
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option}
                  {...tagProps}
                  // Disable chip if user is trying to remove their own 'Admin' group
                  disabled={option === 'Admin' && userEmail === rowEmail}
                />
              );
            })
          }
          disableClearable={true}
          sx={{ minWidth: 250 }}
        />
      );
    },
    [groups, userContext.user]
  );

  /**
   * Columns for DataGrid
   */
  const columns: GridColDef<CognitoUserRow>[] = useMemo(
    () => [
      {
        headerName: 'Email',
        field: 'Attributes',
        width: 300,
        // Use valueGetter to find the "email" attribute
        valueGetter: (attributes: CognitoUser['Attributes']) =>
          attributes?.find(a => a.Name === 'email')?.Value,
        editable: false,
      },
      {
        headerName: 'Groups',
        field: 'Groups',
        width: 400,
        editable: true,
        renderCell: renderGroupsCell,
        renderEditCell: renderGroupsEditCell,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 100,
        getActions: params => {
          const { id } = params;
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
          return isInEditMode
            ? [
                <GridActionsCellItem
                  key="save"
                  icon={<SaveIcon />}
                  label="Save"
                  onClick={handleSaveClick(id)}
                />,
                <GridActionsCellItem
                  key="cancel"
                  icon={<CancelIcon />}
                  label="Cancel"
                  onClick={handleCancelClick(id)}
                  color="inherit"
                />,
              ]
            : [
                <GridActionsCellItem
                  key="edit"
                  icon={<EditIcon />}
                  label="Edit"
                  onClick={handleEditClick(id)}
                  color="inherit"
                />,
              ];
        },
      },
    ],
    [
      rowModesModel,
      renderGroupsCell,
      renderGroupsEditCell,
      handleEditClick,
      handleSaveClick,
      handleCancelClick,
    ]
  );

  return userContext.user ? (
    isUserInGroup(userContext.user, 'Admin') ? (
      <Box ml={3} mr={3} mt={2}>
        <Typography variant="h2" mb={2} fontWeight={400}>
          User Management
        </Typography>
        <ServiceDataGrid
          rowId="Username"
          service={cognitoUserService}
          columns={columns}
          rowSelection={false}
          pageSize={10}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={setRowModesModel}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          showMultiFilterBuilder={false}
          disableColumnFilter={true}
        />
      </Box>
    ) : (
      <div>Unauthorized</div>
    )
  ) : (
    <CircularProgress />
  );
};

export default AdminPage;
